import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { GoogleReviewsResponse } from '../types';
import { api } from '../services/api';

const ReviewsSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #6c757d;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ReviewCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const AuthorAvatar = styled.div<{ $hasImage: boolean; $imageUrl?: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  font-size: 1.2rem;
  ${props => props.$hasImage && props.$imageUrl 
    ? `background-image: url(${props.$imageUrl}); background-size: cover; background-position: center;`
    : `background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);`
  }
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
`;

const ReviewDate = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #6c757d;
`;

const StarRating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const Star = styled.span<{ $filled: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$filled ? '#ffc107' : '#e9ecef'};
  margin-right: 2px;
`;

const ReviewText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  margin: 0;
  font-style: italic;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  margin-top: 3rem;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #6c757d;
  font-weight: 500;
`;

const GoogleLogo = styled.div`
  text-align: center;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
`;

interface GoogleReviewsProps {
  limit?: number;
  minRating?: number;
  showStats?: boolean;
  title?: string;
  subtitle?: string;
}

const GoogleReviews: React.FC<GoogleReviewsProps> = ({
  limit = 6,
  minRating = 4,
  showStats = true,
  title = "O que nossos clientes dizem",
  subtitle = "Veja os depoimentos reais de quem confiou em nosso trabalho"
}) => {
  const [reviews, setReviews] = useState<GoogleReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    checkConfigurationAndFetchReviews();
  }, [limit, minRating]);

  const checkConfigurationAndFetchReviews = async () => {
    try {
      setLoading(true);
      
      // First check if Google Business is enabled and configured
      const settingsResponse = await api.get('/google-business/settings');
      const settings = settingsResponse.data;
      
      if (!settings.enabled || !settings.place_id || !settings.api_key) {
        setConfigured(false);
        setLoading(false);
        return;
      }
      
      setConfigured(true);
      
      // Now fetch reviews
      const response = await api.get(`/google-business/reviews?limit=${limit}&min_rating=${minRating}`);
      setReviews(response.data);
    } catch (err: any) {
      console.error('Error with Google reviews:', err);
      
      // If it's a configuration error, don't show the section
      if (err.response?.status === 404 || err.response?.status === 400) {
        setConfigured(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index} $filled={index < rating}>
        ★
      </Star>
    ));
  };

  const getAuthorInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatRating = (rating: number) => {
    return rating.toFixed(1);
  };

  // Don't render anything if not configured or loading or no reviews
  if (loading || !configured || !reviews || reviews.reviews.length === 0) {
    return null;
  }

  return (
    <ReviewsSection>
      <Container>
        <SectionTitle>{title}</SectionTitle>
        <SectionSubtitle>{subtitle}</SectionSubtitle>

        {showStats && (
          <StatsContainer>
            <StatItem>
              <StatValue>{formatRating(reviews.average_rating)}</StatValue>
              <StatLabel>Avaliação Média</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{reviews.total_count}</StatValue>
              <StatLabel>Total de Avaliações</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{reviews.reviews.length}</StatValue>
              <StatLabel>Depoimentos Destacados</StatLabel>
            </StatItem>
          </StatsContainer>
        )}

        <ReviewsGrid>
          {reviews.reviews.map((review, index) => (
            <ReviewCard key={index}>
              <ReviewHeader>
                <AuthorAvatar 
                  $hasImage={!!review.profile_photo_url}
                  $imageUrl={review.profile_photo_url}
                >
                  {!review.profile_photo_url && getAuthorInitials(review.author_name)}
                </AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{review.author_name}</AuthorName>
                  <ReviewDate>{review.relative_time_description}</ReviewDate>
                </AuthorInfo>
              </ReviewHeader>

              <StarRating>
                {renderStars(review.rating)}
              </StarRating>

              {review.text && (
                <ReviewText>"{review.text}"</ReviewText>
              )}
            </ReviewCard>
          ))}
        </ReviewsGrid>

        <GoogleLogo>
          <span>🔵</span>
          <span>Avaliações verificadas do Google</span>
        </GoogleLogo>
      </Container>
    </ReviewsSection>
  );
};

export default GoogleReviews;